import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import useCreateUpsellRequest from 'modules/upsell/hooks/useCreateUpsellRequest'

export type UpgradeOrigin =
  | 'createPhysicalCard'
  | 'createVirtualCard'
  | 'createPolicy'
  | 'createAccount'
interface UpgradePlanModalProps {
  onClose: () => void
  origin: UpgradeOrigin
}

const useNotifyRequestExpenseAddon = (origin: UpgradeOrigin) => {
  const [success, setSuccess] = React.useState(false)
  const { create, loading: submitting } = useCreateUpsellRequest()
  const onSubmit = async () => {
    const result = await create({ moduleRequested: AvailableModules.expenses })

    setSuccess(handleResult(result, () => undefined).success)
  }

  return {
    submitting,
    onSubmit,
    success,
  }
}

const trackUpgradePlan = (eventName: string, origin: UpgradeOrigin) => {
  const eventData = () => {
    if (eventName === trackingEvents.expenses.goToUpgradeNow) {
      return { origin: 'expenses_upgrade_modal' }
    } else if (eventName === trackingEvents.expenses.upgradeModalView) {
      const reason = () => {
        switch (origin) {
          case 'createPhysicalCard':
            return 'physical_cards'
          case 'createVirtualCard':
            return 'virtual_cards'
          case 'createAccount':
          case 'createPolicy':
            return 'approval_levels'
          default:
            return 'undefined'
        }
      }
      return { reason: reason() }
    } else {
      return {}
    }
  }
  track(eventName, eventData())
}

const UpgradePlanModal = observer(({ onClose, origin }: UpgradePlanModalProps) => {
  const { submitting, onSubmit, success } = useNotifyRequestExpenseAddon(origin)

  useEffect(() => {
    if (success) {
      trackUpgradePlan(trackingEvents.expenses.goToUpgradeNow, origin)
    } else {
      trackUpgradePlan(trackingEvents.expenses.upgradeModalView, origin)
    }
  }, [success])

  return (
    <>
      {success ? (
        <ThankYouModal onClose={onClose} />
      ) : (
        <UpgradeModal onClose={onClose} onSubmit={onSubmit} submitting={submitting} />
      )}
    </>
  )
})

export default UpgradePlanModal

interface UpgradeModalProps {
  onClose: () => void
  onSubmit: () => void
  submitting: boolean
}

const UpgradeModal = ({ onClose, onSubmit, submitting }: UpgradeModalProps) => {
  return (
    <Modal
      title={i18n.t('expenses_v2.upgrade_modal.title')}
      illustration="fireworks"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} type="secondary">
            {i18n.t('expenses_v2.upgrade_modal.actions.cancel')}
          </Button>
          <Button onClick={onSubmit} disabled={submitting} iconName="star">
            {i18n.t('expenses_v2.upgrade_modal.actions.confirm')}
          </Button>
        </>
      }
    >
      <Box>
        <Text.Normal weight="medium" color="grey800">
          {i18n.t('expenses_v2.upgrade_modal.description')}
        </Text.Normal>
      </Box>
    </Modal>
  )
}

interface ThankYouModalProps {
  onClose: () => void
}

const ThankYouModal = ({ onClose }: ThankYouModalProps) => {
  return (
    <Modal
      illustration="contact-us"
      title={i18n.t('expenses_v2.thank_you_modal.title')}
      description={i18n.t('expenses_v2.thank_you_modal.description')}
      onClose={onClose}
      footer={
        <Button type="primary" onClick={onClose}>
          {i18n.t('expenses_v2.thank_you_modal.close')}
        </Button>
      }
    />
  )
}
